import { AnimatePresence, motion } from 'motion/react';
import type { CatalogoEtiquetas, ElementoEtiqueta, Orientacion, UsoEtiqueta } from '../../api';
import { ChipBoton, Icono } from '../../components/ui';
import { normalizarGiro } from '../../lib/giro';
import { aviso, botonSecundario, c, campo, etiquetaCampo, mono, rotulo, tarjeta } from '../../theme';
import { ICONOS, NOMBRES, PASO_MM, acotar } from './comun';
import { abrir, aparecer, desplegar } from '../../components/movimiento';

/**
 * Los paneles del costado del diseñador: propiedades del elemento elegido, la
 * lista de variables para insertar y el tamaño del papel. Viven aparte del
 * lienzo porque no tienen nada que ver con dibujar: son formularios.
 */

export function Campo({
  rotuloTexto,
  valor,
  paso = 0.5,
  onCambiar,
}: {
  rotuloTexto: string;
  valor: number;
  paso?: number;
  onCambiar: (v: number) => void;
}) {
  return (
    <label style={{ ...etiquetaCampo, flex: 1, minWidth: 70 }}>
      {rotuloTexto}
      <input
        type="number"
        inputMode={Number.isInteger(paso) ? 'numeric' : 'decimal'}
        step={paso}
        value={valor}
        onChange={(e) => onCambiar(Number(e.target.value))}
        style={{ ...campo, height: 34 }}
      />
    </label>
  );
}

/**
 * El panel de propiedades cambia con transición al elegir otro elemento: el
 * anterior sale y el nuevo entra. Sin eso el formulario entero se reemplaza de
 * golpe y no se nota que ahora es de otra cosa.
 */
export function Propiedades(props: PropsPropiedades) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      {props.elemento ? (
        <PanelElemento key={props.elemento.id} {...props} elemento={props.elemento} />
      ) : (
        <motion.div key="vacio" {...aparecer} style={{ ...tarjeta, padding: 14 }}>
          <span style={rotulo}>Propiedades</span>
          <div style={{ marginTop: 8, fontSize: 12.5, color: c.texto3, textWrap: 'pretty' }}>
            Elegí un elemento del dibujo para cambiarle el contenido, la posición o el tamaño.
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface PropsPropiedades {
  elemento: ElementoEtiqueta | null;
  medida: { ancho: number; alto: number };
  /** Cómo va a salir impreso el código elegido. Sólo para códigos de barras. */
  barra: { modulo: number; puntos: number; apretado: boolean; necesita: number } | null;
  onProbarCodigo: () => void;
  onCambiar: (campos: Partial<ElementoEtiqueta>) => void;
  onGirar: (giro: number) => void;
  onBorrar: () => void;
  onCapa: (hacia: 'arriba' | 'abajo') => void;
}

function PanelElemento({
  elemento,
  medida,
  barra,
  onProbarCodigo,
  onCambiar,
  onGirar,
  onBorrar,
  onCapa,
}: PropsPropiedades & { elemento: ElementoEtiqueta }) {
  const e = elemento;
  const conContenido = e.tipo === 'texto' || e.tipo === 'barras' || e.tipo === 'qr';

  return (
    <motion.div {...aparecer} style={{ ...tarjeta, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icono nombre={ICONOS[e.tipo]} size={17} color={c.texto3} />
        <span style={{ fontSize: 14, fontWeight: 600, color: c.texto, flex: 1 }}>{NOMBRES[e.tipo]}</span>
        <button
          type="button"
          onClick={() => onCapa('arriba')}
          title="Traer al frente"
          aria-label="Traer al frente"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', display: 'grid' }}
        >
          <Icono nombre="flip_to_front" size={17} color={c.texto3} />
        </button>
        <button
          type="button"
          onClick={() => onCapa('abajo')}
          title="Mandar atrás"
          aria-label="Mandar atrás"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', display: 'grid' }}
        >
          <Icono nombre="flip_to_back" size={17} color={c.texto3} />
        </button>
        <button
          type="button"
          onClick={onBorrar}
          title="Borrar"
          aria-label="Borrar elemento"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', display: 'grid' }}
        >
          <Icono nombre="delete" size={17} color={c.rojo} />
        </button>
      </div>

      {(e.tipo === 'barras' || e.tipo === 'qr') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: c.texto2 }}>Qué código lleva</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {/* Un código de barras casi siempre lleva una de estas cuatro. La
                lista completa está abajo, en «Insertar variable». */}
            {(
              [
                ['Producto', '{{articulo.codigo}}'],
                ['Nº de parte', '{{articulo.parte}}'],
                ['Capacho', '{{capacho.id}}'],
                ['Pallet', '{{pallet.id}}'],
              ] as [string, string][]
            ).map(([nombre, valor]) => (
              <ChipBoton
                key={valor}
                grupo="etq-codigo"
                activo={e.contenido === valor}
                onClick={() => onCambiar({ contenido: valor })}
                title={valor}
                style={{ padding: '4px 9px', fontSize: 11.5, fontWeight: 400, background: c.fondo }}
              >
                {nombre}
              </ChipBoton>
            ))}
          </div>
        </div>
      )}

      {conContenido && (
        <label style={etiquetaCampo}>
          Contenido
          <textarea
            value={e.contenido ?? ''}
            onChange={(ev) => onCambiar({ contenido: ev.target.value })}
            rows={e.tipo === 'texto' ? 3 : 2}
            style={{ ...campo, height: 'auto', padding: 8, fontFamily: mono, fontSize: 12.5, resize: 'vertical' }}
          />
        </label>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Campo rotuloTexto="X (mm)" valor={e.x} onCambiar={(v) => onCambiar({ x: acotar(v, 0, medida.ancho) })} />
        <Campo rotuloTexto="Y (mm)" valor={e.y} onCambiar={(v) => onCambiar({ y: acotar(v, 0, medida.alto) })} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Campo
          rotuloTexto="Ancho"
          valor={e.ancho}
          onCambiar={(v) => onCambiar({ ancho: acotar(v, PASO_MM, medida.ancho - e.x) })}
        />
        <Campo
          rotuloTexto="Alto"
          valor={e.alto}
          onCambiar={(v) => onCambiar({ alto: acotar(v, PASO_MM, medida.alto - e.y) })}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: c.texto2 }}>Giro</span>
        <div style={{ display: 'flex', gap: 5 }}>
          {[0, 90, 180, 270].map((g) => (
            <ChipBoton
              key={g}
              grupo="etq-giro"
              activo={normalizarGiro(e.giro) === g}
              onClick={() => onGirar(g)}
              style={{ ...botonSecundario, flex: 1, minHeight: 32, padding: 0, fontSize: 11.5, justifyContent: 'center', color: normalizarGiro(e.giro) === g ? c.verde : c.texto2 }}
            >
              {g}°
            </ChipBoton>
          ))}
        </div>
        {/* Y cualquier ángulo, para una línea en diagonal o un texto al sesgo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="range"
            min={0}
            max={359}
            value={normalizarGiro(e.giro)}
            onChange={(ev) => onGirar(Number(ev.target.value))}
            aria-label="Giro del elemento"
            style={{ flex: 1, accentColor: c.verde, cursor: 'pointer' }}
          />
          <span style={{ fontFamily: mono, fontSize: 11.5, color: c.texto3, width: 34 }}>
            {normalizarGiro(e.giro)}°
          </span>
        </div>
      </div>

      {e.tipo === 'texto' && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <Campo
              rotuloTexto="Cuerpo (mm)"
              valor={e.cuerpo ?? 4}
              paso={0.5}
              onCambiar={(v) => onCambiar({ cuerpo: acotar(v, 1, 40) })}
            />
            <button
              type="button"
              onClick={() => onCambiar({ negrita: !e.negrita })}
              aria-pressed={e.negrita ?? false}
              style={{
                ...botonSecundario,
                width: 44,
                minHeight: 34,
                padding: 0,
                fontWeight: 700,
                background: e.negrita ? c.verdeSuave : c.panel,
                borderColor: e.negrita ? c.verde : c.bordeFuerte,
                color: e.negrita ? c.verde : c.texto2,
              }}
            >
              N
            </button>
          </div>
          <label style={etiquetaCampo}>
            Alineación
            <select
              value={e.alineacion ?? 'izquierda'}
              onChange={(ev) => onCambiar({ alineacion: ev.target.value as ElementoEtiqueta['alineacion'] })}
              style={{ ...campo, height: 34 }}
            >
              <option value="izquierda">Izquierda</option>
              <option value="centro">Centro</option>
              <option value="derecha">Derecha</option>
            </select>
          </label>
        </>
      )}

      <AnimatePresence initial={false}>
        {e.tipo === 'barras' && barra !== null && (
          <motion.div
            {...desplegar}
            style={{ ...desplegar.style, ...(barra.apretado ? aviso('warn') : { fontSize: 11.5, color: c.texto4, textWrap: 'pretty' }) }}
          >
            Barra más fina: {barra.modulo.toFixed(2)} mm, {barra.puntos}{' '}
            {barra.puntos === 1 ? 'punto' : 'puntos'} de la impresora.{' '}
            {barra.apretado
              ? `Con eso el lector engancha cuando tiene suerte: dale al menos ${Math.ceil(barra.necesita)} mm de ancho, o acortá el contenido.`
              : 'Entra cómodo, con su zona muda a los costados.'}
          </motion.div>
        )}
      </AnimatePresence>

      {(e.tipo === 'barras' || e.tipo === 'qr') && (
        <button type="button" onClick={onProbarCodigo} style={{ ...botonSecundario, minHeight: 36 }}>
          <Icono nombre="barcode_reader" size={16} color={c.texto3} />
          Probar con el lector
        </button>
      )}

      {e.tipo === 'barras' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.texto2 }}>
          <input
            type="checkbox"
            checked={e.mostrarTexto !== false}
            onChange={(ev) => onCambiar({ mostrarTexto: ev.target.checked })}
          />
          Mostrar el código debajo
        </label>
      )}

      {e.tipo === 'qr' && (
        <label style={etiquetaCampo}>
          Corrección de error
          <select
            value={e.correccion ?? 'M'}
            onChange={(ev) => onCambiar({ correccion: ev.target.value })}
            style={{ ...campo, height: 34 }}
          >
            <option value="L">Baja (cabe más texto)</option>
            <option value="M">Media</option>
            <option value="Q">Alta</option>
            <option value="H">Máxima (se lee aunque se manche)</option>
          </select>
        </label>
      )}

      {(e.tipo === 'linea' || e.tipo === 'rectangulo') && (
        <Campo
          rotuloTexto="Grosor (mm)"
          valor={e.grosor ?? 0.4}
          paso={0.1}
          onCambiar={(v) => onCambiar({ grosor: acotar(v, 0.1, 10) })}
        />
      )}

      {e.tipo === 'rectangulo' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.texto2 }}>
          <input type="checkbox" checked={e.relleno ?? false} onChange={(ev) => onCambiar({ relleno: ev.target.checked })} />
          Relleno negro
        </label>
      )}
    </motion.div>
  );
}

export function Variables({
  catalogo,
  uso,
  onInsertar,
}: {
  catalogo: CatalogoEtiquetas;
  /** Las variables dependen de para qué es la etiqueta. */
  uso: UsoEtiqueta;
  onInsertar: (clave: string) => void;
}) {
  return (
    <div style={{ ...tarjeta, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={rotulo}>Insertar variable</span>
      <div style={{ fontSize: 12, color: c.texto3, textWrap: 'pretty' }}>
        Se reemplazan al imprimir con los datos de la mercadería.
      </div>
      {catalogo.variables[uso].map((g) => (
        <div key={g.grupo} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: c.texto2 }}>{g.grupo}</span>
          {/* Un grupo que el sistema no sabe se paga después, cuando el que
              imprime tiene que tipear cada renglón: más vale que se vea acá,
              mientras se está eligiendo qué poner en la etiqueta. */}
          {g.pedidas && (
            <span style={{ fontSize: 11, color: c.ambar, textWrap: 'pretty' }}>
              Se cargan a mano cada vez que se imprime.
            </span>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {g.variables.map((v) => (
              <button
                key={v.clave}
                type="button"
                onClick={() => onInsertar(v.clave)}
                title={`{{${v.clave}}} · por ejemplo ${v.muestra}`}
                style={{
                  padding: '4px 9px',
                  borderRadius: 999,
                  border: `1px solid ${c.borde}`,
                  background: c.fondo,
                  color: c.texto2,
                  fontSize: 11.5,
                  cursor: 'pointer',
                }}
              >
                {v.nombre}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Papel({
  medida,
  orientacion,
  uso,
  catalogo,
  onCambiar,
  onOrientacion,
  onUso,
  fuera,
}: {
  medida: { ancho: number; alto: number };
  orientacion: Orientacion;
  uso: UsoEtiqueta;
  catalogo: CatalogoEtiquetas;
  onCambiar: (m: { ancho: number; alto: number }) => void;
  onOrientacion: (o: Orientacion) => void;
  onUso: (u: UsoEtiqueta) => void;
  /** Cuántos elementos quedaron fuera del área después de girar. */
  fuera: number;
}) {
  return (
    <div style={{ ...tarjeta, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={rotulo}>Para qué es</span>
      <div style={{ display: 'flex', gap: 6 }}>
        {catalogo.usos.map((u) => (
          <ChipBoton
            key={u.id}
            grupo="etq-uso"
            activo={uso === u.id}
            onClick={() => onUso(u.id)}
            title={u.detalle}
            style={{ ...botonSecundario, flex: 1, minHeight: 36, fontSize: 12, justifyContent: 'center', color: uso === u.id ? c.verde : c.texto2 }}
          >
            {u.nombre}
          </ChipBoton>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: c.texto4, textWrap: 'pretty' }}>
        {catalogo.usos.find((u) => u.id === uso)?.detalle}. Cambia qué variables se pueden usar.
      </div>

      <span style={{ ...rotulo, marginTop: 4 }}>Tamaño del papel</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {catalogo.tamanos.map((t) => (
          <ChipBoton
            key={t.id}
            grupo="etq-tamano"
            activo={medida.ancho === t.ancho && medida.alto === t.alto}
            onClick={() => onCambiar({ ancho: t.ancho, alto: t.alto })}
            style={{ padding: '4px 9px', fontSize: 11.5, fontWeight: 400, background: c.fondo }}
          >
            {t.nombre}
          </ChipBoton>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Campo rotuloTexto="Ancho (mm)" valor={medida.ancho} paso={1} onCambiar={(v) => onCambiar({ ...medida, ancho: v })} />
        <Campo rotuloTexto="Alto (mm)" valor={medida.alto} paso={1} onCambiar={(v) => onCambiar({ ...medida, alto: v })} />
      </div>
      <span style={{ ...rotulo, marginTop: 4 }}>Orientación</span>
      <div style={{ display: 'flex', gap: 6 }}>
        {(
          [
            { id: 'vertical' as const, nombre: 'Vertical', icono: 'crop_portrait' },
            { id: 'apaisada' as const, nombre: 'Apaisada', icono: 'crop_landscape' },
          ]
        ).map((o) => (
          <ChipBoton
            key={o.id}
            grupo="etq-orientacion"
            activo={orientacion === o.id}
            onClick={() => onOrientacion(o.id)}
            style={{ ...botonSecundario, flex: 1, minHeight: 36, fontSize: 12, justifyContent: 'center', color: orientacion === o.id ? c.verde : c.texto2 }}
          >
            <Icono nombre={o.icono} size={16} color={orientacion === o.id ? c.verde : c.texto3} />
            {o.nombre}
          </ChipBoton>
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: c.texto4, textWrap: 'pretty' }}>
        {orientacion === 'apaisada'
          ? `El papel sigue siendo de ${medida.ancho} × ${medida.alto} mm: se dibuja a lo largo, sobre ${medida.alto} × ${medida.ancho}, y al imprimir se gira un cuarto de vuelta.`
          : `La ${catalogo.impresora.nombre} admite de ${catalogo.impresora.anchoMm.min} a ${catalogo.impresora.anchoMm.max} mm de ancho.`}
      </div>

      <AnimatePresence initial={false}>
        {fuera > 0 && (
          <motion.div {...abrir(8)} style={aviso('warn')}>
            {fuera === 1 ? 'Un elemento quedó' : `${fuera} elementos quedaron`} fuera del área. Acomodalos antes de
            guardar.
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
